import React, { useEffect, useState } from "react";
import SidebarMember from "../../components/Sidebar/SidebarMember";
import { db, auth } from "../../firebase";
import {
  collection,
  onSnapshot,
  query,
  where,
  updateDoc,
  doc,
  deleteDoc,
  writeBatch,
} from "firebase/firestore";

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [requests, setRequests] = useState([]);
  const [payments, setPayments] = useState([]);
  const [bids, setBids] = useState([]);
  const user = auth.currentUser;

  // Load persisted state from localStorage
  const getPersistedDeletedIds = () => {
    if (!user?.uid) return new Set();
    const stored = localStorage.getItem(`alerts_deleted_${user.uid}`);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  };

  const getPersistedReadIds = () => {
    if (!user?.uid) return new Set();
    const stored = localStorage.getItem(`alerts_read_${user.uid}`);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  };

  const [deletedAlertIds, setDeletedAlertIds] = useState(getPersistedDeletedIds());
  const [readAlertIds, setReadAlertIds] = useState(getPersistedReadIds());

  // Persist to localStorage whenever state changes
  const persistDeletedIds = (ids) => {
    if (!user?.uid) return;
    localStorage.setItem(`alerts_deleted_${user.uid}`, JSON.stringify(Array.from(ids)));
    setDeletedAlertIds(ids);
  };

  const persistReadIds = (ids) => {
    if (!user?.uid) return;
    localStorage.setItem(`alerts_read_${user.uid}`, JSON.stringify(Array.from(ids)));
    setReadAlertIds(ids);
  };

  // Helper function to safely convert to Date
  const toDate = (dateValue) => {
    if (!dateValue) return new Date();
    if (dateValue instanceof Date) return dateValue;
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      return dateValue.toDate();
    }
    if (dateValue.seconds) {
      return new Date(dateValue.seconds * 1000);
    }
    return new Date(dateValue);
  };

  // Fetch groups
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "groups"), (snapshot) => {
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setGroups(data);
    });
    return () => unsub();
  }, []);

  // Fetch join requests
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, "joinRequests"),
      where("memberId", "==", user.uid)
    );
    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRequests(data);
    });
    return () => unsub();
  }, [user]);

  // Fetch payments
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db, "payments"), where("memberId", "==", user.uid));
    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setPayments(data);
    });
    return () => unsub();
  }, [user]);

  // Fetch bids (fund requests)
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db, "bids"), where("memberId", "==", user.uid));
    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setBids(data);
    });
    return () => unsub();
  }, [user]);

  // Generate alerts based on data
  useEffect(() => {
    if (!user?.uid || groups.length === 0) {
      setLoading(false);
      return;
    }

    const generatedAlerts = [];

    // Get active groups (accepted requests)
    const activeRequests = requests.filter((req) => req.status === "accepted");

    activeRequests.forEach((req) => {
      const group = groups.find((g) => g.id === req.groupId);
      if (!group) return;

      // Get last payment for this group
      const groupPayments = payments
        .filter((p) => p.groupId === req.groupId)
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

      const lastPayment = groupPayments[0];
      const lastPaidDate = lastPayment?.createdAt?.toDate
        ? new Date(lastPayment.createdAt.seconds * 1000)
        : req.lastPaidAt?.toDate
        ? new Date(req.lastPaidAt.seconds * 1000)
        : null;

      // Calculate due date (30 days from last payment or from request acceptance)
      const dueDate = lastPaidDate
        ? new Date(lastPaidDate.getTime() + 30 * 24 * 60 * 60 * 1000)
        : req.createdAt?.toDate
        ? new Date(req.createdAt.seconds * 1000 + 30 * 24 * 60 * 60 * 1000)
        : null;

      if (!dueDate) return;

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dueDateOnly = new Date(dueDate);
      dueDateOnly.setHours(0, 0, 0, 0);
      const daysDiff = Math.floor((today.getTime() - dueDateOnly.getTime()) / (1000 * 60 * 60 * 24));

      // A. PAYMENT DUE ALERT (due today)
      if (daysDiff === 0) {
        generatedAlerts.push({
          id: `payment_due_${req.groupId}`,
          type: "payment_due",
          message: `Your monthly payment of ₹${group.contribution || 0} is due today.`,
          priority: "high",
          groupId: group.id,
          groupName: group.groupName,
          amount: group.contribution,
          createdAt: new Date(),
          read: false,
        });
      }

      // B. PAYMENT OVERDUE ALERT (after due date)
      if (daysDiff > 0 && daysDiff <= 1) {
        generatedAlerts.push({
          id: `payment_overdue_${req.groupId}`,
          type: "payment_overdue",
          message: `Your payment is overdue. Please complete it immediately.`,
          priority: "critical",
          groupId: group.id,
          groupName: group.groupName,
          amount: group.contribution,
          createdAt: new Date(),
          read: false,
        });
      }

      // C. FINAL REMINDER ALERT (2 days after due date)
      if (daysDiff === 2) {
        generatedAlerts.push({
          id: `final_reminder_${req.groupId}`,
          type: "final_reminder",
          message: `Final reminder! Please complete your payment within 24 hours.`,
          priority: "critical",
          groupId: group.id,
          groupName: group.groupName,
          amount: group.contribution,
          createdAt: new Date(),
          read: false,
        });
      }
    });

    // D. JOIN REQUEST STATUS ALERT
    requests.forEach((req) => {
      if (req.status === "accepted" && !req.notificationRead) {
        const group = groups.find((g) => g.id === req.groupId);
        generatedAlerts.push({
          id: `join_accepted_${req.id}`,
          type: "join_status",
          message: `Your request to join ${group?.groupName || "the group"} has been accepted.`,
          priority: "medium",
          groupId: req.groupId,
          groupName: group?.groupName,
          status: "accepted",
          createdAt: req.createdAt?.toDate || new Date(),
          read: false,
        });
      } else if (req.status === "rejected" && !req.notificationRead) {
        const group = groups.find((g) => g.id === req.groupId);
        generatedAlerts.push({
          id: `join_rejected_${req.id}`,
          type: "join_status",
          message: `Your request to join ${group?.groupName || "the group"} has been rejected.`,
          priority: "medium",
          groupId: req.groupId,
          groupName: group?.groupName,
          status: "rejected",
          createdAt: req.createdAt?.toDate || new Date(),
          read: false,
        });
      }
    });

    // E. FUND REQUEST STATUS ALERT
    bids.forEach((bid) => {
      if (bid.status === "approved" && !bid.notificationRead) {
        generatedAlerts.push({
          id: `fund_approved_${bid.id}`,
          type: "fund_status",
          message: `You are approved to take the fund for ${bid.month || "this month"}.`,
          priority: "high",
          groupId: bid.groupId,
          groupName: bid.groupName,
          status: "approved",
          createdAt: bid.createdAt?.toDate || new Date(),
          read: false,
        });
      } else if (bid.status === "rejected" && !bid.notificationRead) {
        generatedAlerts.push({
          id: `fund_rejected_${bid.id}`,
          type: "fund_status",
          message: `Your fund request was rejected.`,
          priority: "medium",
          groupId: bid.groupId,
          groupName: bid.groupName,
          status: "rejected",
          createdAt: bid.createdAt?.toDate || new Date(),
          read: false,
        });
      }
    });

    // F. GROUP START ALERT (when group becomes active - first payment due)
    activeRequests.forEach((req) => {
      const group = groups.find((g) => g.id === req.groupId);
      const groupPayments = payments.filter((p) => p.groupId === req.groupId);
      
      // If no payments made yet and group is active
      if (groupPayments.length === 0 && group && group.currentMembers > 0) {
        const requestDate = toDate(req.createdAt);
        const daysSinceRequest = Math.floor((Date.now() - requestDate.getTime()) / (1000 * 60 * 60 * 24));
        
        // Show alert if group started recently (within last 7 days)
        if (daysSinceRequest <= 7) {
          generatedAlerts.push({
            id: `group_start_${req.groupId}`,
            type: "group_start",
            message: `Your group has started. Monthly payments are now open.`,
            priority: "medium",
            groupId: group.id,
            groupName: group.groupName,
            createdAt: requestDate,
            read: false,
          });
        }
      }
    });

    // Fetch existing notifications from Firestore and merge
    const q = query(collection(db, "notifications"), where("to", "==", user.uid));
    const unsub = onSnapshot(q, (snapshot) => {
      const firestoreAlerts = snapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          createdAt: toDate(data.createdAt),
        };
      });

      // Ensure all generated alerts have proper Date objects and filter out deleted ones
      // Also apply read status from persistent state
      const normalizedGeneratedAlerts = generatedAlerts
        .filter(alert => !deletedAlertIds.has(alert.id))
        .map(alert => ({
          ...alert,
          createdAt: toDate(alert.createdAt),
          read: readAlertIds.has(alert.id) ? true : alert.read || false,
        }));

      // Merge generated alerts with Firestore notifications
      // Prioritize Firestore notifications, but include generated alerts
      const allAlerts = [...firestoreAlerts, ...normalizedGeneratedAlerts];

      // Remove duplicates (same type and groupId)
      const uniqueAlerts = allAlerts.reduce((acc, alert) => {
        const key = `${alert.type}_${alert.groupId || alert.id}`;
        const alertDate = toDate(alert.createdAt);
        const existingDate = acc[key] ? toDate(acc[key].createdAt) : null;
        
        if (!acc[key] || alertDate.getTime() > existingDate.getTime()) {
          acc[key] = {
            ...alert,
            createdAt: alertDate,
          };
        }
        return acc;
      }, {});

      // Sort by date (newest first)
      const sortedAlerts = Object.values(uniqueAlerts).sort((a, b) => {
        const dateA = toDate(a.createdAt);
        const dateB = toDate(b.createdAt);
        return dateB.getTime() - dateA.getTime();
      });

      setAlerts(sortedAlerts);
      setLoading(false);
    });

    return () => unsub();
  }, [user, groups, requests, payments, bids, deletedAlertIds, readAlertIds]);

  // Mark alert as read
  const markAsRead = async (alertId) => {
    try {
      // Check if it's a Firestore notification
      const alert = alerts.find((a) => a.id === alertId);
      if (alert && alert.to) {
        // It's a Firestore notification
        const alertRef = doc(db, "notifications", alertId);
        await updateDoc(alertRef, { read: true });
      } else {
        // It's a generated alert - add to read set and persist to localStorage
        const newReadIds = new Set([...readAlertIds, alertId]);
        persistReadIds(newReadIds);
        // Also update current alerts immediately for instant feedback
        setAlerts((prev) =>
          prev.map((a) => (a.id === alertId ? { ...a, read: true } : a))
        );
      }
    } catch (error) {
      console.error("Error marking alert as read:", error);
    }
  };

  // Mark all alerts as read
  const markAllAsRead = async () => {
    try {
      const batch = writeBatch(db);
      let hasUpdates = false;

      alerts.forEach((alert) => {
        if (!alert.read && alert.to) {
          // It's a Firestore notification
          const alertRef = doc(db, "notifications", alert.id);
          batch.update(alertRef, { read: true });
          hasUpdates = true;
        }
      });

      if (hasUpdates) {
        await batch.commit();
      }

      // Update read state for generated alerts
      const generatedAlertIds = alerts.filter(a => !a.to && !a.read).map(a => a.id);
      if (generatedAlertIds.length > 0) {
        const newReadIds = new Set([...readAlertIds, ...generatedAlertIds]);
        persistReadIds(newReadIds);
      }
      // Also update current alerts immediately
      setAlerts((prev) => prev.map((a) => ({ ...a, read: true })));
    } catch (error) {
      console.error("Error marking all alerts as read:", error);
    }
  };

  // Delete a single alert
  const deleteAlert = async (alertId) => {
    try {
      const alert = alerts.find((a) => a.id === alertId);
      if (!alert) return;

      // Check if it's a Firestore notification
      if (alert.to) {
        // It's a Firestore notification - delete from Firestore
        const alertRef = doc(db, "notifications", alertId);
        await deleteDoc(alertRef);
        // Remove from local state immediately
        setAlerts((prev) => prev.filter((a) => a.id !== alertId));
      } else {
        // It's a generated alert - add to deleted set and persist to localStorage
        const newDeletedIds = new Set([...deletedAlertIds, alertId]);
        persistDeletedIds(newDeletedIds);
        // Also remove from current alerts immediately
        setAlerts((prev) => prev.filter((a) => a.id !== alertId));
      }
    } catch (error) {
      console.error("Error deleting alert:", error);
      alert("Failed to delete alert. Please try again.");
    }
  };

  // Delete all alerts
  const deleteAllAlerts = async () => {
    if (!window.confirm("Are you sure you want to delete all alerts? This action cannot be undone.")) {
      return;
    }

    try {
      const batch = writeBatch(db);
      let hasDeletes = false;

      // Delete all Firestore notifications
      alerts.forEach((alert) => {
        if (alert.to) {
          // It's a Firestore notification
          const alertRef = doc(db, "notifications", alert.id);
          batch.delete(alertRef);
          hasDeletes = true;
        }
      });

      if (hasDeletes) {
        await batch.commit();
      }

      // Mark all generated alerts as deleted
      const generatedAlertIds = alerts.filter(a => !a.to).map(a => a.id);
      if (generatedAlertIds.length > 0) {
        const newDeletedIds = new Set([...deletedAlertIds, ...generatedAlertIds]);
        persistDeletedIds(newDeletedIds);
      }

      // Clear all alerts from local state
      setAlerts([]);
    } catch (error) {
      console.error("Error deleting all alerts:", error);
      alert("Failed to delete all alerts. Please try again.");
    }
  };

  // Get alert color based on type and priority
  const getAlertColor = (type, priority) => {
    if (priority === "critical") return { bg: "#fee2e2", border: "#fca5a5", icon: "🔴" };
    if (priority === "high") return { bg: "#fef3c7", border: "#fcd34d", icon: "🟡" };
    if (type === "join_status" || type === "fund_status") {
      return { bg: "#d1fae5", border: "#6ee7b7", icon: "✅" };
    }
    return { bg: "#dbeafe", border: "#93c5fd", icon: "ℹ️" };
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return "N/A";
    const d = toDate(date);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const unreadCount = alerts.filter((a) => !a.read).length;

  // Styles
  const styles = {
    page: {
      flex: 1,
      padding: "30px",
      backgroundColor: "#f8fafc",
      minHeight: "100vh",
      overflowY: "auto",
      marginLeft: "220px",
      width: "calc(100% - 220px)",
      boxSizing: "border-box",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "30px",
    },
    pageTitle: {
      fontSize: "28px",
      fontWeight: "600",
      color: "#111827",
      margin: "0 0 8px 0",
    },
    subtitle: {
      fontSize: "14px",
      color: "#6b7280",
      margin: 0,
    },
    headerActions: {
      display: "flex",
      gap: "12px",
    },
    actionButton: {
      padding: "10px 20px",
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      border: "1px solid #e5e7eb",
      backgroundColor: "#fff",
      color: "#374151",
      transition: "all 0.2s ease",
    },
    unreadBadge: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "24px",
      height: "24px",
      padding: "0 8px",
      borderRadius: "12px",
      backgroundColor: "#ef4444",
      color: "#fff",
      fontSize: "12px",
      fontWeight: "600",
      marginLeft: "12px",
    },
    alertsList: {
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    },
    alertCard: {
      backgroundColor: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: "12px",
      padding: "20px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      transition: "all 0.2s ease",
      position: "relative",
    },
    alertCardUnread: {
      borderLeft: "4px solid",
      backgroundColor: "#fefefe",
    },
    alertHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: "12px",
    },
    alertIcon: {
      fontSize: "24px",
      marginRight: "12px",
    },
    alertContent: {
      flex: 1,
    },
    alertMessage: {
      fontSize: "16px",
      fontWeight: "500",
      color: "#111827",
      margin: "0 0 8px 0",
      lineHeight: "1.5",
    },
    alertMeta: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: "12px",
    },
    alertDate: {
      fontSize: "12px",
      color: "#6b7280",
    },
    alertActions: {
      display: "flex",
      gap: "8px",
    },
    markReadButton: {
      padding: "6px 12px",
      borderRadius: "6px",
      fontSize: "12px",
      fontWeight: "500",
      cursor: "pointer",
      border: "1px solid #e5e7eb",
      backgroundColor: "#fff",
      color: "#6b7280",
      transition: "all 0.2s ease",
    },
    deleteButton: {
      padding: "6px 12px",
      borderRadius: "6px",
      fontSize: "12px",
      fontWeight: "500",
      cursor: "pointer",
      border: "1px solid #fee2e2",
      backgroundColor: "#fff",
      color: "#dc2626",
      transition: "all 0.2s ease",
    },
    deleteAllButton: {
      padding: "10px 20px",
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      border: "1px solid #fee2e2",
      backgroundColor: "#fff",
      color: "#dc2626",
      transition: "all 0.2s ease",
    },
    emptyState: {
      textAlign: "center",
      padding: "60px 20px",
      color: "#6b7280",
    },
    emptyStateIcon: {
      fontSize: "64px",
      marginBottom: "16px",
      opacity: 0.5,
    },
    emptyStateText: {
      fontSize: "18px",
      fontWeight: "500",
      color: "#9ca3af",
      margin: 0,
    },
    loadingState: {
      textAlign: "center",
      padding: "60px 20px",
      color: "#6b7280",
      fontSize: "16px",
    },
  };

  return (
    <div style={{ display: "flex", width: "100%", minHeight: "100vh" }}>
      <SidebarMember />
      <div style={styles.page}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.pageTitle}>
              Alerts
              {unreadCount > 0 && <span style={styles.unreadBadge}>{unreadCount}</span>}
            </h1>
            <p style={styles.subtitle}>Stay updated with important notifications</p>
          </div>
          {alerts.length > 0 && (
            <div style={styles.headerActions}>
              {unreadCount > 0 && (
                <button
                  style={styles.actionButton}
                  onClick={markAllAsRead}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f3f4f6";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#fff";
                  }}
                >
                  Mark All as Read
                </button>
              )}
              <button
                style={styles.deleteAllButton}
                onClick={deleteAllAlerts}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#fee2e2";
                  e.target.style.color = "#991b1b";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#fff";
                  e.target.style.color = "#dc2626";
                }}
              >
                Delete All
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div style={styles.loadingState}>Loading alerts...</div>
        ) : alerts.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyStateIcon}>🔔</div>
            <p style={styles.emptyStateText}>No alerts at the moment</p>
            <p style={{ ...styles.emptyStateText, fontSize: "14px", marginTop: "8px" }}>
              You're all caught up!
            </p>
          </div>
        ) : (
          <div style={styles.alertsList}>
            {alerts.map((alert) => {
              const colors = getAlertColor(alert.type, alert.priority);
              const isUnread = !alert.read;

              return (
                <div
                  key={alert.id}
                  style={{
                    ...styles.alertCard,
                    ...(isUnread ? styles.alertCardUnread : {}),
                    borderLeftColor: isUnread ? colors.border : "transparent",
                    backgroundColor: isUnread ? colors.bg : "#fff",
                  }}
                >
                  <div style={styles.alertHeader}>
                    <div style={{ display: "flex", alignItems: "flex-start", flex: 1 }}>
                      <span style={styles.alertIcon}>{colors.icon}</span>
                      <div style={styles.alertContent}>
                        <p style={styles.alertMessage}>{alert.message}</p>
                        {alert.groupName && (
                          <p style={{ fontSize: "14px", color: "#6b7280", margin: "4px 0 0 0" }}>
                            Group: {alert.groupName}
                          </p>
                        )}
                      </div>
                    </div>
                    {isUnread && (
                      <span
                        style={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          backgroundColor: "#ef4444",
                          flexShrink: 0,
                          marginLeft: "12px",
                        }}
                      />
                    )}
                  </div>
                  <div style={styles.alertMeta}>
                    <span style={styles.alertDate}>{formatDate(alert.createdAt)}</span>
                    <div style={styles.alertActions}>
                      {isUnread && (
                        <button
                          style={styles.markReadButton}
                          onClick={() => markAsRead(alert.id)}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = "#f3f4f6";
                            e.target.style.color = "#374151";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = "#fff";
                            e.target.style.color = "#6b7280";
                          }}
                        >
                          Mark as Read
                        </button>
                      )}
                      <button
                        style={styles.deleteButton}
                        onClick={() => {
                          if (window.confirm("Are you sure you want to delete this alert?")) {
                            deleteAlert(alert.id);
                          }
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#fee2e2";
                          e.target.style.color = "#991b1b";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "#fff";
                          e.target.style.color = "#dc2626";
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
